import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from 'react';
import { PropTypes } from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import * as Sentry from '@sentry/browser';
import moment from 'moment';
import format from 'string-template';
import 'react-toastify/dist/ReactToastify.css';

// Hooks
import useAuthentication, {
  SignInMethod,
  AuthErrorCode,
  SIGN_IN_LINK_STAGING_MESSAGE,
} from '../../hooks/useAuthentication';
import useNavigation from '../../hooks/useNavigation';
import useSessionStore from '../../hooks/useSessionStore';

// Models
import User from '../../Model/User';
import Coach from '../../Model/Coach';

// Utils
import { firestorePaths } from '../../utils/paths';
import {
  anonId,
  contractId,
  externalCoach,
  leadId,
  planCode,
} from '../../utils/onboarding';
import { getQueryVariable } from '../../utils/queryParams';
import { isStaging } from '../../utils/environment';
import { ActiveSubscriptionClientStatuses } from '../../CoachTools/utils/statusFilter';

// Contexts
import CustomerIOContext from '../CustomerIOContext';
import UserContractContext from '../UserContractContext';
import FirebaseContext from '../FirebaseContext';

import useOnboardingFlowStateReducer, { initialState } from './useOnboardingFlowStateReducer';
import {
  StyledToastContainer,
  CreatingAccountOverlay,
  CreatingAccountMessage,
} from './styles';
import texts from './texts.json';

const FUTURE_DATE_PARAM_FORMAT = 'YYYY-MM-DD';

const SignInLinkStatus = {
  SIGNING_IN: 'signingIn',
  SENT: 'sent',
  EXPIRED: 'expired',
  REFUSED: 'refused',
  FAILED: 'failed',
};

// Left in the URL, a reload would retry the sign-in with an already spent code.
const FIREBASE_LINK_PARAMS = ['mode', 'oobCode', 'apiKey', 'lang'];

// navigateTo keeps the current search when given '', so an emptied one must be cleared explicitly.
const withoutFirebaseLinkParams = (currentSearch) => {
  const params = new URLSearchParams(currentSearch);
  FIREBASE_LINK_PARAMS.forEach((param) => params.delete(param));
  const remaining = params.toString();
  return remaining ? { search: `?${remaining}` } : { clearSearch: true };
};

const OnboardingFlowContext = React.createContext(initialState);

const Provider = ({
  children,
}) => {
  const [
    state, ,
    setters,
  ] = useOnboardingFlowStateReducer();
  const sessionStore = useSessionStore();
  const { userId: currentUserId } = sessionStore;

  const {
    getSignInMethod,
    createAnonymousAccountWithEmail,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signInWithCustomToken,
    isSignInWithEmailLink,
    signInWithEmailLink,
    signOut,
  } = useAuthentication(sessionStore);

  /*
    Seeded from the URL rather than set in the redeem effect below: effects run after the browser
    has painted, which is long enough to show the account form to someone already on their way in.
    The effect clears both again on every path where it turns out not to redeem.
  */
  const [isCreatingAccount, setIsCreatingAccount] = useState(isSignInWithEmailLink);
  const [signInLinkState, setSignInLinkState] = useState(
    () => (isSignInWithEmailLink() ? { status: SignInLinkStatus.SIGNING_IN } : {}),
  );
  const signInLinkRedeemed = useRef(false);

  const { setEmail: setEmailForCustomerIO } = useContext(CustomerIOContext);
  const { contractDoc } = useContext(UserContractContext);
  const { firebase: { remote, auth } } = useContext(FirebaseContext);

  const {
    search,
    routes,
    pathname,
    navigateTo,
  } = useNavigation();

  const {
    email,
  } = state;

  // Set startAtDate state from a contract or query param
  useEffect(() => {
    // Check if we have a startDate in a contract.
    let { startDate } = contractDoc || {};

    // If we don't have a startDate, check the query params.
    if (!startDate) {
      startDate = getQueryVariable('startAt');
    }

    if (startDate) {
      const futureDate = moment(startDate, FUTURE_DATE_PARAM_FORMAT, true);

      if (futureDate.isValid()) {
        // Check if the date is in the future
        const todayStartOfDayDate = moment().startOf('day');
        if (futureDate.diff(todayStartOfDayDate) > 0) {
          // It is a valid future date!
          setters.setStartAtDate(futureDate);
        }
      }
    }
  }, [
    setters,
    contractDoc,
  ]);

  const userDoc = useMemo(() => {
    if (currentUserId) {
      return new User(() => `${firestorePaths.USER}/${currentUserId}`);
    }
    return null;
  }, [
    currentUserId,
  ]);

  const userDocHasData = !!userDoc && userDoc.hasData;

  useEffect(() => {
    if (anonId) {
      window.analytics.user().anonymousId(anonId);
    }
  }, []);

  useEffect(() => {
    if (userDoc && !userDocHasData) {
      userDoc.init({
        fetch: false,
      });
    }
  }, [
    userDoc,
    userDocHasData,
  ]);

  useEffect(() => {
    if (email) {
      setEmailForCustomerIO(email);
    }
  }, [
    email,
    setEmailForCustomerIO,
  ]);

  // Update onboarding states with user doc data
  useEffect(() => {
    if (contractDoc) {
      setters.setEmail(contractDoc.userEmail || '');
      setters.setFirstName(contractDoc.userFirstName || contractDoc.userName || '');
      setters.setLastName(contractDoc.userLastName || '');
      setters.setPhoneNumber(contractDoc.phoneNumber || '');
    }
    if (userDocHasData) {
      setters.setEmail(userDoc.email || '');
      setters.setFirstName(userDoc.firstName || '');
      setters.setLastName(userDoc.lastName || '');
      setters.setPhoneNumber(userDoc.phoneNumber || '');
    }
  }, [
    userDocHasData,
    setters,
    userDoc,
    contractDoc,
  ]);

  /*
    The address the sign-in link is minted for. The email field is editable, so the client may
    have typed something other than the contract's, and the link is minted for what they typed.
  */
  const resolveSignInLinkEmail = useCallback(() => (
    (getQueryVariable('email') || contractDoc?.userEmail || '').toLowerCase()
  ), [contractDoc]);

  /**
   * Emails a sign-in link that returns to this sign-up page with the contract still in the URL.
   * Called before the client is signed in, so it borrows a throwaway anonymous identity for the
   * ID token the endpoint requires, exactly as coach sign-up does.
   *
   * @param {string} emailForLink The address to send to
   * @returns {Promise<boolean>} True when the link was sent
   */
  const sendCheckoutSignInLink = useCallback(async (emailForLink) => {
    let anonymousUser = null;
    try {
      ({ user: anonymousUser } = await auth.signInAnonymously());
      const response = await remote('generateSignInLink/onboarding', {
        email: emailForLink,
        contractId,
        leadId,
        plan: planCode,
        externalCoach,
        coupon: getQueryVariable('coupon'),
        anonid: anonId,
        startAt: getQueryVariable('startAt'),
      });
      const { success } = await response.json();
      return !!success;
    } catch (err) {
      Sentry.captureException(err);
      return false;
    } finally {
      if (anonymousUser) {
        // Cleanup failure must not change what the send returned; callers only handle a boolean.
        try {
          await anonymousUser.delete();
        } catch (err) {
          Sentry.captureException(err);
        }
      }
    }
  }, [auth, remote]);

  // Runs after any sign-in of an existing account, whether by password or by emailed link.
  const applyReturningClientRules = useCallback(async (signedInUser, signedInEmail) => {
    const commonErrorFields = {
      isError: true,
      errorMsg: texts.emailInUse,
      route: routes.LOGIN,
      routeSearch: search,
    };
    const commonRouteStateFields = {
      userEmail: signedInEmail,
      onLogin: pathname,
    };
    const existingUserDoc = await User.getById(signedInUser.uid);
    const existingCoachIds = existingUserDoc?.coachIds ?? [];
    const newCoachId = contractDoc?.coach;

    // if the user has no coach assigned, allow the signup
    if (!existingCoachIds.length) {
      sessionStore.changeUser(signedInUser);
      return { isError: false };
    }

    if (newCoachId && existingCoachIds.includes(newCoachId)) {
      /*
        A client may hold several subscriptions with the same coach, one per product,
        so an existing subscription only blocks this signup when it is for the SAME
        product. Without a contracts map the product can't be resolved, so it falls back
        to this coach's own live flag, and to `subscriptionStatus` only on a single-coach
        doc where that field can only mean this coach: wrongly blocking a purchase is
        recoverable, charging twice is not.
      */
      const purchasedProductId = contractDoc?.productId;
      const canScopeToProduct = !!purchasedProductId && !!existingUserDoc?.hasContractEntries;
      const isRepeatOfLiveProduct = canScopeToProduct
        ? !!existingUserDoc.getLiveContractForProduct(newCoachId, purchasedProductId)
        : existingUserDoc.activeCoachIds.includes(newCoachId)
          || (existingCoachIds.length === 1
            && ActiveSubscriptionClientStatuses.includes(existingUserDoc?.subscriptionStatus));

      if (existingUserDoc && isRepeatOfLiveProduct) {
        let coachName = '';
        try {
          const coach = await Coach.getCoachById(newCoachId);
          coachName = coach?.name || '';
        } catch (coachLookupErr) {
          Sentry.captureException(coachLookupErr);
        }
        await signOut();
        return {
          isError: true,
          errorMsg: coachName
            ? format(texts.programAlreadyActiveWithCoach, { coachName })
            : texts.programAlreadyActive,
        };
      }
      sessionStore.changeUser(signedInUser);
      return { isError: false };
    }

    // Unable to determine the new coach
    if (!newCoachId) {
      // No contract context to drive a swap; treat as a plain email-in-use case.
      await signOut();
      return {
        ...commonErrorFields,
        routeState: {
          ...commonRouteStateFields,
        },
      };
    }

    // Start flow for multi-coach signup
    setIsCreatingAccount(true);
    let multiCoachResponse;
    try {
      const response = await remote('userProfiles-onMultiCoachSignup', { newCoachId });
      multiCoachResponse = await response.json();
    } catch (err) {
      Sentry.captureException(err);
      await signOut();
      return {
        isError: true,
        isRetryable: true,
        errorMsg: texts.multiCoachSignupError,
      };
    }

    /*
      The new coach joins the signed-in account. A token comes back only when a sibling account
      already holds this coach, and the buyer becomes that account.
    */
    if (!multiCoachResponse?.customToken) {
      if (multiCoachResponse?.userId !== signedInUser.uid) {
        Sentry.captureException(new Error('onMultiCoachSignup resolved an unexpected account'), {
          extra: { multiCoachResponse },
        });
        await signOut();
        return {
          isError: true,
          isRetryable: true,
          errorMsg: texts.multiCoachSignupError,
        };
      }

      sessionStore.changeUser(signedInUser);
      return { isError: false };
    }

    try {
      await signOut();
      const switchedUser = await signInWithCustomToken(multiCoachResponse.customToken);
      sessionStore.changeUser(switchedUser);
      return { isError: false };
    } catch (err) {
      Sentry.captureException(err);
      return {
        isError: true,
        isRetryable: true,
        errorMsg: texts.multiCoachSignupError,
      };
    }
  }, [
    contractDoc,
    sessionStore,
    remote,
    signOut,
    signInWithCustomToken,
    routes.LOGIN,
    search,
    pathname,
  ]);

  /**
   * This function creates an user on firebase auth and firestore.
   * It returns an object that indicates if the operation was succesful or not,
   * with some extra data to handle errors.
   */
  const createUser = useCallback(async ({
    firstName: submittedFirstName,
    lastName: submittedLastName,
    email: submittedEmail,
    phoneNumber: submittedPhoneNumber,
    password: submittedPassword,
  }) => {
    const emailForRegistration = (submittedEmail || email)?.toLowerCase();
    const signInMethod = await getSignInMethod(emailForRegistration);

    const commonErrorFields = {
      isError: true,
      errorMsg: texts.emailInUse,
      route: routes.LOGIN,
      routeSearch: search,
    };
    const commonRouteStateFields = {
      userEmail: emailForRegistration,
      onLogin: pathname,
    };

    // With enumeration protection on, fetchSignInMethodsForEmail answers nothing and only NO_ACCOUNT is reachable.
    const emailExistingAccountSignInLink = async () => {
      // Staging mocks the SendGrid send, so the link would never arrive.
      if (isStaging) {
        return { isError: true, errorMsg: SIGN_IN_LINK_STAGING_MESSAGE };
      }
      if (!contractDoc) {
        return {
          ...commonErrorFields,
          routeState: {
            ...commonRouteStateFields,
            sendAuthLink: true,
          },
        };
      }
      const sent = await sendCheckoutSignInLink(emailForRegistration);
      if (!sent) {
        return { isError: true, errorMsg: texts.signInLinkFailed };
      }
      setSignInLinkState({ status: SignInLinkStatus.SENT, email: emailForRegistration });
      return { isError: false };
    };

    switch (signInMethod) {
      case SignInMethod.NO_ACCOUNT: {
        try {
          const user = submittedPassword
            ? await createUserWithEmailAndPassword(emailForRegistration, submittedPassword)
            : await createAnonymousAccountWithEmail(emailForRegistration);
          await User.createUser(user.uid, {
            firstName: submittedFirstName,
            lastName: submittedLastName,
            email: submittedEmail,
            phoneNumber: submittedPhoneNumber,
          });
          sessionStore.changeUser(user);
          return { isError: false };
        } catch (err) {
          if (err.code === AuthErrorCode.EMAIL_ALREADY_IN_USE) {
            return emailExistingAccountSignInLink();
          }
          Sentry.captureException(err);
          return {
            isError: true,
            errorMsg: texts.accountCreateError,
          };
        }
      }
      case SignInMethod.EMAIL_LINK:
        return emailExistingAccountSignInLink();
      case SignInMethod.EMAIL_PASSWORD: {
        try {
          let signedInUser;
          try {
            signedInUser = await signInWithEmailAndPassword(emailForRegistration, submittedPassword);
          } catch (err) {
            return {
              isError: true,
              errorMsg: texts.wrongPassword,
              showForgotPassword: true,
            };
          }

          return applyReturningClientRules(signedInUser, emailForRegistration);
        } finally {
          setIsCreatingAccount(false);
        }
      }
      default:
        return {
          ...commonErrorFields,
          routeState: {
            ...commonRouteStateFields,
          },
        };
    }
  }, [
    routes,
    createAnonymousAccountWithEmail,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    getSignInMethod,
    email,
    sessionStore,
    pathname,
    search,
    contractDoc,
    sendCheckoutSignInLink,
    applyReturningClientRules,
  ]);

  /*
    Gated on a resolved contract: without one there is nothing to return to, and redeeming spends
    the code for nothing. Both signals for "somebody is already signed in" are needed:
    auth.currentUser reads null until Firebase finishes restoring its session, while the store is
    hydrated synchronously from storage on the first render (Auth.jsx:23-35). Redeeming while
    another session is still restoring would sign that person out and the emailed client in.
  */
  useEffect(() => {
    if (signInLinkRedeemed.current || !isSignInWithEmailLink()) {
      return;
    }

    const emailForLink = contractDoc ? resolveSignInLinkEmail() : '';
    if (!contractDoc || currentUserId || auth.currentUser || !emailForLink) {
      signInLinkRedeemed.current = true;
      setSignInLinkState({});
      setIsCreatingAccount(false);
      return;
    }

    signInLinkRedeemed.current = true;
    const redeem = async () => {
      let user;
      try {
        ({ user } = await signInWithEmailLink(emailForLink));
      } catch (err) {
        Sentry.captureException(err);
        setSignInLinkState({ status: SignInLinkStatus.EXPIRED, email: emailForLink });
        setIsCreatingAccount(false);
        return;
      }

      const result = await applyReturningClientRules(user, emailForLink);

      navigateTo(pathname, {
        ...withoutFirebaseLinkParams(search),
        replace: true,
      });
      if (!result.isError) {
        return;
      }
      if (result.route) {
        navigateTo(result.route, { search: result.routeSearch, state: result.routeState });
        return;
      }
      setSignInLinkState({
        status: result.isRetryable ? SignInLinkStatus.FAILED : SignInLinkStatus.REFUSED,
        email: emailForLink,
        message: result.errorMsg,
      });
      setIsCreatingAccount(false);
    };
    redeem();
  }, [
    contractDoc,
    isSignInWithEmailLink,
    currentUserId,
    auth,
    resolveSignInLinkEmail,
    signInWithEmailLink,
    applyReturningClientRules,
    navigateTo,
    pathname,
    search,
  ]);

  /*
    changeUser only notifies listeners; the session itself lands a few hundred ms later. The
    sign-in is not finished until it does, or the account step reappears in between.
  */
  useEffect(() => {
    if (currentUserId && signInLinkState.status === SignInLinkStatus.SIGNING_IN) {
      setSignInLinkState({});
      setIsCreatingAccount(false);
    }
  }, [
    currentUserId,
    signInLinkState.status,
  ]);

  const resendSignInLink = useCallback(async () => {
    const emailForLink = signInLinkState.email || resolveSignInLinkEmail();
    if (!emailForLink) {
      return;
    }
    setSignInLinkState((current) => ({ ...current, isSending: true, resendResult: null }));
    const sent = await sendCheckoutSignInLink(emailForLink);
    // Keeps the status, so an expiry does not fall back to the first-time copy.
    setSignInLinkState((current) => ({
      ...current,
      email: emailForLink,
      isSending: false,
      resendResult: sent ? 'sent' : 'failed',
    }));
  }, [signInLinkState.email, resolveSignInLinkEmail, sendCheckoutSignInLink]);

  const resetSignInLinkState = useCallback(() => setSignInLinkState({}), []);

  const contextValue = useMemo(() => ({
    setters,
    ...state,
    createUser,
    signInLinkState,
    resendSignInLink,
    resetSignInLinkState,
  }), [
    state,
    setters,
    createUser,
    signInLinkState,
    resendSignInLink,
    resetSignInLinkState,
  ]);

  return (
    <OnboardingFlowContext.Provider value={contextValue}>
      <>
        <StyledToastContainer />
        {children}
        {isCreatingAccount && (
          <CreatingAccountOverlay
            messageComponent={(
              <CreatingAccountMessage>
                {signInLinkState.status === SignInLinkStatus.SIGNING_IN
                  ? texts.signingYouIn
                  : texts.creatingAccount}
              </CreatingAccountMessage>
            )}
          />
        )}
      </>
    </OnboardingFlowContext.Provider>
  );
};

Provider.propTypes = {
  children: PropTypes.element.isRequired,
};

export default OnboardingFlowContext;
export { SignInLinkStatus };
export const OnBoardingFlowContextProvider = compose(
  observer,
)(Provider);
